import type { TokenStorageGateway } from "@shared-kernel/application/ports/token-storage-gateway";
import type {
  SharedRepository,
  ReportAnIssueBody,
  UserVM,
  AccountRoleVM,
  UpdateUserBody,
  PayoutLessonVM,
  OnboardStudentBody,
  OnboardTeacherBody,
  OnboardProviderBody,
  PayoutLessonsContext,
} from "@shared-kernel/application/ports/shared/shared-repository";
import type { DocumentaryResourceVM } from "@shared-kernel/domain/types/documentary-resource";
import { HttpGateway } from "@shared-kernel/secondary/http-gateway";
import config from "../../../../config/backend";

export class BackendSharedRepository extends HttpGateway implements SharedRepository {
  private _userRoute: string = "v1/users";
  private _accountsRoute: string = "v1/accounts";
  private _adminRoute: string = "v1/admin";
  private _fundingRequestRoute: string = "v1/student/funding-requests";
  private _teacherRoute: string = "v1/teacher";
  private _documentaryResourcesRoute: string = "v1/shared/documentary-resources";

  constructor(tokenStorageGateway: TokenStorageGateway) {
    super(config.url, tokenStorageGateway);
  }

  async update(body: UpdateUserBody): Promise<void> {
    const route = `${this._userRoute}/profile`;
    await this._instance.put(route, body);
  }

  async profile(): Promise<UserVM> {
    const { data: user } = await this._instance.get(`${this._accountsRoute}/profile`);
    return user;
  }

  async accountRoles(): Promise<AccountRoleVM[]> {
    const { data } = await this._instance.get(`${this._accountsRoute}/roles`);
    return data;
  }

  async isEmailUsed(email: string): Promise<void> {
    await this._instance.head(`${this._adminRoute}/users/${email}`);
  }

  async uploadProfilePicture(file: File): Promise<string> {
    const formDataBody = new FormData();
    formDataBody.append("image", file);

    const res = await this._instance.post(`${this._userRoute}/profile-pictures:upload`, formDataBody);
    return res.data;
  }

  async reportIssue(body: ReportAnIssueBody): Promise<void> {
    const route = `${this._userRoute}/issues:report`;
    await this._instance.post(route, body);
  }

  async onboardStudent(body: OnboardStudentBody): Promise<void> {
    await this._instance.post(`${this._userRoute}/onboarding/student`, body);
  }

  async onboardTeacher(body: OnboardTeacherBody): Promise<void> {
    await this._instance.post(`${this._userRoute}/onboarding/teacher`, body);
  }

  async onboardProvider(body: OnboardProviderBody): Promise<void> {
    await this._instance.post(`${this._userRoute}/onboarding/provider`, body);
  }

  async uploadDocument(file: File): Promise<string> {
    const formDataBody = new FormData();
    formDataBody.append("document", file);

    const res = await this._instance.post(`${this._fundingRequestRoute}/documents:upload`, formDataBody);
    return res.data;
  }

  async getPayoutLessons(payoutId: string, context: PayoutLessonsContext): Promise<PayoutLessonVM[]> {
    const route = context.asAdmin ? `${this._adminRoute}/payouts/${payoutId}/lessons` : `${this._teacherRoute}/payouts/${payoutId}/lessons`;
    const response = await this._instance.get(route);
    return response.data;
  }

  async getVisibleDocumentaryResources(): Promise<DocumentaryResourceVM[]> {
    const { data: resources } = await this._instance.get(this._documentaryResourcesRoute);
    return resources;
  }

  async documentaryResourceConsultationLog(resourceId: string): Promise<void> {
    await this._instance.post(`${this._documentaryResourcesRoute}/${resourceId}:consult`);
  }
}
